import type { AppLogEntry } from './dto.js';

const REASON_KEYS = ['reason', 'message', 'error', 'detail'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function firstString(record: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

/** Texto acionável do log — `meta.reason`, `meta.message` ou `meta.body.message`. */
export function logEntryReason(entry: AppLogEntry): string | null {
  const meta = entry.meta;
  if (!meta) return null;

  const direct = firstString(meta, REASON_KEYS);
  if (direct) return direct;

  const body = meta.body;
  if (typeof body === 'string' && body.trim()) return body;
  if (isRecord(body)) return firstString(body, REASON_KEYS);

  return null;
}

/** Código de erro no topo ou em `meta.body.code` (http.exception). */
export function logEntryErrorCode(entry: AppLogEntry): string | null {
  if (typeof entry.errorCode === 'string' && entry.errorCode.trim()) return entry.errorCode;
  const meta = entry.meta;
  if (!meta) return null;
  if (typeof meta.code === 'string' && meta.code.trim()) return meta.code;
  const body = meta.body;
  if (isRecord(body) && typeof body.code === 'string' && body.code.trim()) return body.code;
  return null;
}

/**
 * Meta que ainda não foi promovida a motivo/código — o que a UI pode mostrar
 * como chips ou JSON expansível, sem repetir o texto já visível.
 */
export function logEntryMetaExtras(entry: AppLogEntry): Record<string, unknown> | null {
  const meta = entry.meta;
  if (!meta) return null;

  const extras: Record<string, unknown> = { ...meta };
  const reason = logEntryReason(entry);
  const code = logEntryErrorCode(entry);

  stripDisplayed(extras, reason, code);

  const body = extras.body;
  if (isRecord(body)) {
    const nextBody = { ...body };
    stripDisplayed(nextBody, reason, code);
    if (Object.keys(nextBody).length === 0) delete extras.body;
    else extras.body = nextBody;
  } else if (typeof body === 'string' && body === reason) {
    delete extras.body;
  }

  return Object.keys(extras).length > 0 ? extras : null;
}

function stripDisplayed(
  record: Record<string, unknown>,
  reason: string | null,
  code: string | null,
): void {
  for (const key of REASON_KEYS) {
    if (typeof record[key] === 'string' && record[key] === reason) delete record[key];
  }
  if (typeof record.code === 'string' && record.code === code) delete record.code;
}
